import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, Subscription, switchMap, tap } from 'rxjs';

import { ApiService } from 'anatoliapps-angular/services';
import { Cart, CartSchema, CheckoutResponse, CheckoutResponseScheme, Contact } from 'hakikat/models';

import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';

enum CartErrorMessages {
  GET_FAILED = 'Failed to get cart.',
  ADD_FAILED = 'Failed to add item to cart.',
  CLEAR_FAILED = 'Failed to clear cart.',
  UPDATE_FAILED = 'Failed to update item in cart.',
  DELETE_FAILED = 'Failed to delete item from cart.',

  INVALID_QUANTITY = 'Product quantity must be positive',
  PRODUCT_NOT_FOUND = 'Product to remove was not found in cart'
}

export interface CartItemInput {
  pid: string;
  price?: number;
  quantity?: number;

  contact?: Contact;
  attributes?: Record<string, any>;
};

export interface CheckoutInput {
  contact: Contact;
};

@Injectable({
  providedIn: 'root'
})
export class CartService extends ApiService implements OnDestroy {

  //#region Cart Properties
  #cart: BehaviorSubject<Cart | null> = new BehaviorSubject<Cart | null>(null);;
  cart$: Observable<Cart | null> = this.#cart.asObservable();

  get currentCart(): Cart | null {
    return this.#cart.value;
  }
  //#endregion

  //#region Endpoints
  readonly #CART_ENDPOINT = 'cart';
  //#endregion

  //#region Services
  readonly #authService = inject(AuthService);
  readonly #authSubscription: Subscription;
  //#endregion

  //#region Constructor and Initialization
  constructor() {
    super(environment.apiUrl);

    // Subscribe to user changes to update the cart
    this.#authSubscription = this.#authService.user$.pipe(
      filter(user => !!user),
      switchMap(() => this.getCart())
    ).subscribe();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnDestroy(): void {
    this.#authSubscription.unsubscribe();
  }
  //#endregion

  //#region Cart Methods
  public getCart(): Observable<Cart> {
    return this.get<Cart>(this.#CART_ENDPOINT).pipe(
      map(response => CartSchema.parse(response)),
      tap({
        next: (cart: Cart) => this.#cart.next(cart),
        error: () => console.error(CartErrorMessages.GET_FAILED)
      })
    );
  }

  public addToCart(item: CartItemInput): Observable<Cart> {
    const quantity = item.quantity ?? 1;
    if (quantity <= 0) throw new Error(CartErrorMessages.INVALID_QUANTITY);
    return this.post<Cart>(this.#CART_ENDPOINT, { ...item, quantity }).pipe(
      map(response => CartSchema.parse(response)),
      tap({
        next: (cart: Cart) => this.#cart.next(cart),
        error: (error) => console.error(CartErrorMessages.ADD_FAILED, error)
      })
    );
  }

  public clearCart(): Observable<Cart> {
    return this.delete<Cart>(this.#CART_ENDPOINT).pipe(
      map(response => CartSchema.parse(response)),
      tap({
        next: (cart: Cart) => this.#cart.next(cart),
        error: (error) => console.error(CartErrorMessages.CLEAR_FAILED, error)
      })
    );
  }

  public updateItemQuantity(itemId: string, quantity: number): Observable<Cart> {
    if (quantity <= 0 || quantity % 1 !== 0) throw new Error(CartErrorMessages.INVALID_QUANTITY);

    return this.put<Cart>(`${this.#CART_ENDPOINT}/${itemId}`, { quantity }).pipe(
      map(response => CartSchema.parse(response)),
      tap({
        next: (cart: Cart) => this.#cart.next(cart),
        error: (error) => console.error(CartErrorMessages.UPDATE_FAILED, error)
      })
    );
  }

  public removeFromCart(itemId: string): Observable<Cart> {
    const hasItem = this.#cart.value?.items.some(item => item.id === itemId);
    if (!hasItem) throw new Error(CartErrorMessages.PRODUCT_NOT_FOUND);

    return this.delete(`${this.#CART_ENDPOINT}/${itemId}`).pipe(
      map(response => CartSchema.parse(response)),
      tap({
        next: (cart: Cart) => this.#cart.next(cart),
        error: (error) => console.error(CartErrorMessages.DELETE_FAILED)
      })
    );
  }
  //#endregion

  //#region Checkout Methods
  public checkout(input: CheckoutInput): Observable<any> {
    return this.post<CheckoutResponse>(`${this.#CART_ENDPOINT}/checkout`, input).pipe(
      map((response: CheckoutResponse) => CheckoutResponseScheme.parse(response))
    );
  }
  //#endregion
}
